import { Injectable } from '@angular/core';
import { LocalStorageKeys } from '../models/auth.models';

type ProcessTabGuardResult = {
	blocked: boolean;
	ownerTabId: string;
	record: ProcessTabRecord | null;
};

type ProcessTabRecord = {
	processUuid: string;
	deviceId: string;
	tabId: string;
	/** Epoch ms; ausencia = lock legado → se trata como caducado. */
	updatedAt?: number;
};

@Injectable({
	providedIn: 'root',
})
export class ProcessTabGuardService {
	private readonly storagePrefix = LocalStorageKeys.process_tab_guard_prefix;
	private readonly tabIdSessionKey = LocalStorageKeys.process_tab_id_session_key;
	/** Ventana sin heartbeat tras la cual el lock se considera zombie (cierre de pestaña). */
	private readonly ttlMs = 15_000;
	private readonly heartbeatMs = 3_000;
	private tabId: string | null = null;
	private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
	private heartbeatProcessUuid: string | null = null;
	private heartbeatDeviceId: string | null = null;
	private unloadBound = false;
	private readonly onPageHide = (event: PageTransitionEvent): void => {
		// BFCache: no liberar. Cierre real / navegación: liberar.
		if (event.persisted) return;
		this.releaseOwnedProcessesOnUnload();
	};
	private readonly onBeforeUnload = (): void => {
		this.releaseOwnedProcessesOnUnload();
	};

	/**
	 * Registra la pestaña actual como dueña del proceso (solo si aún no hay dueño vivo).
	 * Lock zombie (sin heartbeat / TTL) se reclama automáticamente.
	 */
	public tryActivateProcess(processUuid: string, deviceId: string): ProcessTabGuardResult {
		this.ensureUnloadListeners();
		const normalizedProcess = processUuid?.trim();
		const normalizedDevice = deviceId?.trim();
		const ownerTabId = this.getTabId();

		if (!normalizedProcess || !normalizedDevice) {
			return { blocked: false, ownerTabId, record: null };
		}

		const key = this.buildProcessKey(normalizedProcess);
		const currentRecord = this.readRecord(key);

		if (!currentRecord || this.isStale(currentRecord)) {
			const nextRecord = this.buildRecord(normalizedProcess, normalizedDevice, ownerTabId);
			this.writeRecord(key, nextRecord);
			this.startHeartbeat(normalizedProcess, normalizedDevice);
			return { blocked: false, ownerTabId, record: nextRecord };
		}

		if (currentRecord.tabId === ownerTabId) {
			this.touchRecord(key, currentRecord);
			this.startHeartbeat(normalizedProcess, normalizedDevice);
			return { blocked: false, ownerTabId, record: currentRecord };
		}

		this.stopHeartbeat();
		return { blocked: true, ownerTabId, record: currentRecord };
	}

	public isBlockedByAnotherTab(processUuid: string): boolean {
		const normalizedProcess = processUuid?.trim();
		if (!normalizedProcess) return false;
		const currentRecord = this.readRecord(this.buildProcessKey(normalizedProcess));
		if (!currentRecord || this.isStale(currentRecord)) return false;
		return currentRecord.tabId !== this.getTabId();
	}

	/** Reclama el proceso para la pestaña actual (p. ej. el usuario cerró las otras pestañas). */
	public takeoverProcess(processUuid: string, deviceId: string): ProcessTabRecord | null {
		this.ensureUnloadListeners();
		const normalizedProcess = processUuid?.trim();
		const normalizedDevice = deviceId?.trim();
		if (!normalizedProcess || !normalizedDevice) {
			return null;
		}

		const key = this.buildProcessKey(normalizedProcess);
		const nextRecord = this.buildRecord(normalizedProcess, normalizedDevice, this.getTabId());
		this.writeRecord(key, nextRecord);
		this.startHeartbeat(normalizedProcess, normalizedDevice);
		return nextRecord;
	}

	public releaseProcess(processUuid: string): void {
		const normalizedProcess = processUuid?.trim();
		if (!normalizedProcess) return;
		const key = this.buildProcessKey(normalizedProcess);
		const currentRecord = this.readRecord(key);
		if (!currentRecord) return;
		if (currentRecord.tabId !== this.getTabId()) return;
		this.removeRecord(key);
		if (this.heartbeatProcessUuid === normalizedProcess) {
			this.stopHeartbeat();
		}
	}

	public stopHeartbeat(): void {
		if (this.heartbeatTimer !== null) {
			clearInterval(this.heartbeatTimer);
			this.heartbeatTimer = null;
		}
		this.heartbeatProcessUuid = null;
		this.heartbeatDeviceId = null;
	}

	private startHeartbeat(processUuid: string, deviceId: string): void {
		if (this.heartbeatProcessUuid === processUuid && this.heartbeatTimer !== null) {
			this.heartbeatDeviceId = deviceId;
			return;
		}
		this.stopHeartbeat();
		this.heartbeatProcessUuid = processUuid;
		this.heartbeatDeviceId = deviceId;
		this.heartbeatTimer = setInterval(() => {
			const uuid = this.heartbeatProcessUuid;
			const device = this.heartbeatDeviceId;
			if (!uuid || !device) return;
			const key = this.buildProcessKey(uuid);
			const current = this.readRecord(key);
			if (!current || current.tabId !== this.getTabId()) {
				this.stopHeartbeat();
				return;
			}
			this.touchRecord(key, current);
		}, this.heartbeatMs);
	}

	private releaseOwnedProcessesOnUnload(): void {
		const uuid = this.heartbeatProcessUuid;
		if (uuid) {
			this.releaseProcess(uuid);
		}
		this.stopHeartbeat();
	}

	private ensureUnloadListeners(): void {
		if (this.unloadBound || typeof window === 'undefined') return;
		this.unloadBound = true;
		window.addEventListener('pagehide', this.onPageHide);
		window.addEventListener('beforeunload', this.onBeforeUnload);
	}

	private isStale(record: ProcessTabRecord): boolean {
		if (typeof record.updatedAt !== 'number' || !Number.isFinite(record.updatedAt)) {
			return true;
		}
		return Date.now() - record.updatedAt > this.ttlMs;
	}

	private buildRecord(processUuid: string, deviceId: string, tabId: string): ProcessTabRecord {
		return {
			processUuid,
			deviceId,
			tabId,
			updatedAt: Date.now(),
		};
	}

	private touchRecord(key: string, record: ProcessTabRecord): void {
		this.writeRecord(key, { ...record, updatedAt: Date.now() });
	}

	private buildProcessKey(processUuid: string): string {
		return `${this.storagePrefix}:${processUuid}`;
	}

	private readRecord(key: string): ProcessTabRecord | null {
		try {
			const raw = localStorage.getItem(key);
			if (!raw) return null;
			const parsed = JSON.parse(raw) as Partial<ProcessTabRecord>;
			if (
				typeof parsed?.processUuid !== 'string' ||
				typeof parsed?.deviceId !== 'string' ||
				typeof parsed?.tabId !== 'string'
			) {
				return null;
			}
			return parsed as ProcessTabRecord;
		} catch {
			return null;
		}
	}

	private writeRecord(key: string, record: ProcessTabRecord): void {
		try {
			localStorage.setItem(key, JSON.stringify(record));
		} catch {
			// localStorage puede estar deshabilitado en algunos contextos.
		}
	}

	private removeRecord(key: string): void {
		try {
			localStorage.removeItem(key);
		} catch {
			// no-op
		}
	}

	private getTabId(): string {
		if (this.tabId) return this.tabId;
		const fromSession = this.readSessionValue(this.tabIdSessionKey);
		if (fromSession) {
			this.tabId = fromSession;
			return fromSession;
		}

		const created =
			typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
				? crypto.randomUUID()
				: `tab-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
		this.tabId = created;
		this.writeSessionValue(this.tabIdSessionKey, created);
		return created;
	}

	private readSessionValue(key: string): string | null {
		try {
			const value = sessionStorage.getItem(key)?.trim();
			return value || null;
		} catch {
			return null;
		}
	}

	private writeSessionValue(key: string, value: string): void {
		try {
			sessionStorage.setItem(key, value);
		} catch {
			// no-op
		}
	}
}
